import React, { useState, useEffect, useContext } from 'react';
import {
    Box, Flex, Heading, Text, Button, Select, VStack, HStack, useToast, Card, CardBody,
    Stat, StatLabel, StatNumber, SimpleGrid, Badge, Progress, Divider, Table, Thead, Tbody, Tr, Th, Td, IconButton, Tooltip,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, FormControl, FormLabel, Textarea
} from '@chakra-ui/react';
import { FiPlay, FiUsers, FiDownload, FiClock, FiFileText, FiMessageSquare } from 'react-icons/fi';
import attendanceService from '../services/attendanceService';
import { fetchCourses } from '../services/courseService';
import AuthContext from '../services/AuthContext';
import * as XLSX from 'xlsx';

const AttendanceControl = () => {
    const { user } = useContext(AuthContext);
    const [courses, setCourses] = useState([]);
    const [groupSettings, setGroupSettings] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [activeSessions, setActiveSessions] = useState([]);
    const [recentSessions, setRecentSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [downloadingSessionId, setDownloadingSessionId] = useState(null);
    const [sendingSmsSessionId, setSendingSmsSessionId] = useState(null);
    const [isSmsModalOpen, setIsSmsModalOpen] = useState(false);
    const [customMessage, setCustomMessage] = useState('');
    const [filterMatier, setFilterMatier] = useState('');
    const [currentTime, setCurrentTime] = useState(new Date());
    const toast = useToast();

    useEffect(() => {
        loadData();
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [coursesData, groupsData] = await Promise.all([
                fetchCourses(),
                attendanceService.getWhatsAppGroups()
            ]);
            // If instructor, filter courses
            const myCourses = user.role === 'admin' ? coursesData : coursesData.filter(c => String(c.teacherId) === String(user.id));
            setCourses(myCourses);
            setGroupSettings(groupsData);
            await loadRecentSessions();
        } catch (error) {
            toast({ title: 'خطأ في تحميل البيانات', status: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const loadRecentSessions = async (matier = '') => {
        try {
            const sessions = await attendanceService.getRecentSessions(matier);
            setRecentSessions(sessions);

            // Collect all active sessions
            const active = sessions.filter(s => s.isActive && new Date(s.endTime) > new Date());
            setActiveSessions(active);
        } catch (error) {
            console.error("Error loading recent sessions", error);
        }
    };

    const handleStart = async () => {
        if (!selectedCourse || !selectedGroup) {
            toast({ title: 'يرجى اختيار المادة والمجموعة', status: 'warning' });
            return;
        }

        // Check if this specific combo is already active
        const isAlreadyActive = activeSessions.some(s => s.matier === selectedCourse && s.group === selectedGroup);
        if (isAlreadyActive) {
            toast({ title: 'هذه الجلسة نشطة بالفعل لهذه المجموعة', status: 'warning' });
            return;
        }

        try {
            const session = await attendanceService.startAttendance({
                matier: selectedCourse,
                group: selectedGroup,
                durationMinutes: 3
            });
            await loadRecentSessions();
            toast({ title: 'تم بدء جلسة Zoom بنجاح!', status: 'success' });
        } catch (error) {
            toast({ title: 'خطأ في بدء الجلسة', description: error.message, status: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadReport = async (sessionId, matier, group) => {
        try {
            setDownloadingSessionId(sessionId);
            const data = await attendanceService.getAbsentees(sessionId);

            if (!data || data.length === 0) {
                toast({ title: 'لا يوجد غائبين لهذه الجلسة', status: 'info' });
                return;
            }

            const excelData = data.map(item => ({
                'Date (التاريخ)': item.date,
                'Time (الوقت)': item.time,
                'Subject (المادة)': item.matier,
                'Group (المجموعة)': item.group,
                'Student Name (اسم الطالب)': item.studentName,
                'Student Email (البريد الإلكتروني)': item.studentEmail,
                'Student Phone (رقم الهاتف)': item.studentPhone,
                'Parent Phone (رقم ولي الأمر)': item.parentPhone
            }));

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Absents');

            const dateStr = new Date().toISOString().split('T')[0];
            const filename = `تقرير_غياب_${matier}_${group}_${dateStr}.xlsx`;

            XLSX.writeFile(workbook, filename);
            toast({ title: 'تم تحميل التقرير بنجاح', status: 'success' });
        } catch (error) {
            toast({
                title: 'خطأ في تحميل التقرير',
                description: error.response?.data?.message || 'فشل في تحميل البيانات',
                status: 'error'
            });
        } finally {
            setDownloadingSessionId(null);
        }
    };

    const handleSendSMS = async () => {
        if (!sendingSmsSessionId) return;
        try {
            const response = await attendanceService.sendAbsentSMS(sendingSmsSessionId, {
                customMessage: customMessage
            });

            if (response.success) {
                toast({
                    title: 'تم إرسال الرسائل بنجاح',
                    description: `تمت معالجة ${response.results?.length || 0} طالباً`,
                    status: 'success'
                });
                setIsSmsModalOpen(false);
            } else {
                toast({
                    title: 'خطأ في إرسال الرسائل',
                    description: response.message || 'حدث خطأ غير متوقع',
                    status: 'error'
                });
            }
        } catch (error) {
            toast({
                title: 'خطأ في الاتصال',
                description: error.response?.data?.message || 'فشل في إرسال الطلب',
                status: 'error'
            });
        } finally {
            setSendingSmsSessionId(null);
        }
    };

    const openSmsModal = (sessionId, matier) => {
        setSendingSmsSessionId(sessionId);
        setCustomMessage(`تعلمكم منصة كوبرا أن ابنكم {studentName} قد تغيب عن الدراسة في مادة ${matier} بتاريخ اليوم.\n\nواجبنا اعلامكم و إحاطتكم بسجل حضور ابنائكم\n\nشكرًا لتعاونكم.\nCobra éducation`);
        setIsSmsModalOpen(true);
    };

    const formatTimeLeft = (endTime) => {
        const remaining = Math.max(0, Math.floor((new Date(endTime) - currentTime) / 1000));
        const mins = Math.floor(remaining / 60);
        const secs = remaining % 60;
        return `${mins}:${String(secs).padStart(2, '0')}`;
    };

    const getProgressValue = (endTime) => {
        const total = 3 * 60; // 3 minutes standard
        const remaining = Math.max(0, Math.floor((new Date(endTime) - currentTime) / 1000));
        return (remaining / total) * 100;
    };

    const availableGroups = groupSettings.find(g => g.matier === selectedCourse)?.groups || [];

    return (
        <Box dir="rtl" fontFamily="Cairo, sans-serif">
            <Heading mb={8} size={{ base: "lg", md: "xl" }} color="blue.700">مركز التحكم في الحضور</Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 8 }}>
                <Card variant="outline" boxShadow="md" borderRadius="xl">
                    <CardBody>
                        <VStack spacing={6} align="stretch">
                            <Heading size="md" color="blue.600">بدء جلسة جديدة</Heading>
                            <Box>
                                <Text mb={2} fontWeight="bold">اختر المادة</Text>
                                <Select
                                    placeholder="اختر المادة"
                                    value={selectedCourse}
                                    onChange={(e) => { setSelectedCourse(e.target.value); setSelectedGroup(''); }}
                                >
                                    {courses.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                                </Select>
                            </Box>
                            <Box>
                                <Text mb={2} fontWeight="bold">اختر مجموعة الواتساب</Text>
                                <Select
                                    placeholder="اختر المجموعة"
                                    value={selectedGroup}
                                    onChange={(e) => setSelectedGroup(e.target.value)}
                                    isDisabled={!selectedCourse}
                                >
                                    {availableGroups.map((g, i) => <option key={i} value={g}>{g}</option>)}
                                </Select>
                            </Box>
                            <Button
                                leftIcon={<FiPlay />}
                                colorScheme="blue"
                                size="lg"
                                onClick={handleStart}
                                isLoading={isLoading}
                                borderRadius="lg"
                                w="100%"
                            >
                                بدء تسجيل حضور جديد
                            </Button>
                        </VStack>
                    </CardBody>
                </Card>

                <VStack spacing={4} align="stretch">
                    <Heading size="md" color="blue.600">الجلسات النشطة ({activeSessions.length})</Heading>
                    {activeSessions.length > 0 ? (
                        activeSessions.map(session => (
                            <Card key={session._id} bg="blue.50" variant="outline" boxShadow="sm" borderRadius="xl">
                                <CardBody p={4}>
                                    <VStack spacing={3} align="stretch">
                                        <Flex justify="space-between" align="center">
                                            <Box>
                                                <Text fontWeight="bold" fontSize="md">{session.matier}</Text>
                                                <Text fontSize="sm" color="gray.600">المجموعة: {session.group}</Text>
                                            </Box>
                                            <Stat textAlign="left">
                                                <StatNumber fontSize="xl" color="red.500">
                                                    {formatTimeLeft(session.endTime)}
                                                </StatNumber>
                                            </Stat>
                                        </Flex>
                                        <Progress
                                            value={getProgressValue(session.endTime)}
                                            size="xs"
                                            colorScheme="blue"
                                            borderRadius="full"
                                        />
                                    </VStack>
                                </CardBody>
                            </Card>
                        ))
                    ) : (
                        <Card p={8} bg="gray.50" variant="outline" borderRadius="xl" borderStyle="dashed">
                            <VStack spacing={2}>
                                <FiUsers size={32} color="#CBD5E0" />
                                <Text color="gray.500">لا توجد جلسات نشطة حالياً</Text>
                            </VStack>
                        </Card>
                    )}
                </VStack>
            </SimpleGrid>

            <Divider my={10} />

            <Box mt={4}>
                <HStack mb={4} justify="space-between" flexWrap="wrap" spacing={4}>
                    <Heading size="md" color="blue.600">الجلسات الأخيرة والتقارير</Heading>
                    <HStack>
                        <Select
                            placeholder="فلترة: كل المواد"
                            value={filterMatier}
                            onChange={(e) => { setFilterMatier(e.target.value); loadRecentSessions(e.target.value); }}
                            size="sm"
                            w="200px"
                            borderRadius="lg"
                        >
                            {courses.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                        </Select>
                        {filterMatier && (
                            <Button size="sm" variant="ghost" colorScheme="red" onClick={() => { setFilterMatier(''); loadRecentSessions(''); }}>
                                مسح الفلتر
                            </Button>
                        )}
                    </HStack>
                </HStack>
                <Card variant="outline" borderRadius="xl" overflow="hidden">
                    <Box overflowX="auto">
                        <Table variant="simple" size={{ base: "sm", md: "md" }}>
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th>المادة</Th>
                                    <Th display={{ base: 'none', md: 'table-cell' }}>الأستاذ</Th>
                                    <Th>المجموعة</Th>
                                    <Th display={{ base: 'none', md: 'table-cell' }}>التاريخ</Th>
                                    <Th>الحالة</Th>
                                    <Th>تقرير الغائبين</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {recentSessions.length > 0 ? (
                                    recentSessions.map((session) => (
                                        <Tr key={session._id}>
                                            <Td fontWeight="bold">{session.matier}</Td>
                                            <Td display={{ base: 'none', md: 'table-cell' }} color="gray.500" fontSize="sm">{session.instructorId?.username || '—'}</Td>
                                            <Td>{session.group}</Td>
                                            <Td display={{ base: 'none', md: 'table-cell' }}>{new Date(session.createdAt).toLocaleString('fr-FR')}</Td>
                                            <Td>
                                                {session.isActive && new Date(session.endTime) > new Date() ? (
                                                    <Badge colorScheme="green">نشط</Badge>
                                                ) : (
                                                    <Badge colorScheme="gray">منتهي</Badge>
                                                )}
                                            </Td>
                                            <Td>
                                                <Button
                                                    onClick={() => handleDownloadReport(session._id, session.matier, session.group)}
                                                    size="sm"
                                                    leftIcon={<FiDownload />}
                                                    colorScheme="green"
                                                    variant="ghost"
                                                    isLoading={downloadingSessionId === session._id}
                                                    isDisabled={session.isActive && new Date(session.endTime) > new Date()}
                                                >
                                                    <Text display={{ base: 'none', sm: 'inline' }}>تحميل Excel</Text>
                                                </Button>
                                                <Button
                                                    onClick={() => openSmsModal(session._id, session.matier)}
                                                    size="sm"
                                                    leftIcon={<FiMessageSquare />}
                                                    colorScheme="blue"
                                                    variant="ghost"
                                                    isDisabled={session.isActive && new Date(session.endTime) > new Date()}
                                                    ml={2}
                                                >
                                                    <Text display={{ base: 'none', sm: 'inline' }}>إرسال SMS</Text>
                                                </Button>
                                            </Td>
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                                            لا توجد جلسات سابقة لعرضها
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </Box>
                </Card>
            </Box>

            <Modal isOpen={isSmsModalOpen} onClose={() => setIsSmsModalOpen(false)} size={{ base: "full", md: "xl", lg: "2xl" }} isCentered>
                <ModalOverlay backdropFilter="blur(5px)" bg="blackAlpha.600" />
                <ModalContent borderRadius="2xl" overflow="hidden" m={{ base: 0, md: 4 }} maxH="100vh" dir="rtl">
                    <ModalHeader
                        bgGradient="linear(to-r, orange.400, red.400)"
                        color="white"
                        borderTopRadius={{ base: "none", md: "2xl" }}
                        textAlign="right"
                        fontWeight="700"
                        py={4}
                        px={6}
                    >
                        📱 إرسال رسالة SMS للغائبين
                    </ModalHeader>
                    <ModalCloseButton color="white" top={3} left={3} right="auto" />
                    <ModalBody py={5} px={6}>
                        <VStack spacing={4} align="stretch">
                            <Text color="gray.600" fontSize="sm" textAlign="right">
                                سيتم إرسال رسالة عربية لأولياء أمور الطلاب الغائبين في هذه الجلسة.
                            </Text>

                            <FormControl mt={2}>
                                <HStack justify="space-between" mb={2} flexWrap="wrap">
                                    <FormLabel fontSize="sm" fontWeight="bold" m={0}>نص الرسالة:</FormLabel>
                                    <HStack spacing={2}>
                                        <Button
                                            size="xs"
                                            colorScheme="blue"
                                            variant="outline"
                                            onClick={() => setCustomMessage(prev => prev + ' {studentName}')}
                                        >
                                            إدراج اسم الطالب
                                        </Button>
                                    </HStack>
                                </HStack>
                                <Textarea
                                    value={customMessage}
                                    onChange={(e) => setCustomMessage(e.target.value)}
                                    placeholder="مرحباً... يرجى العلم بأن..."
                                    rows={5}
                                    fontSize="md"
                                    bg="white"
                                    _dark={{ bg: 'gray.800' }}
                                    border="2px solid"
                                    borderColor="blue.300"
                                    borderRadius="xl"
                                    boxShadow="sm"
                                    focusBorderColor="blue.500"
                                    p={3}
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter gap={3} justifyContent="center" pb={5}>
                        <Button
                            bgGradient="linear(to-r, blue.400, purple.400)"
                            color="white"
                            borderRadius="full"
                            fontWeight="700"
                            size="md"
                            px={6}
                            isLoading={sendingSmsSessionId !== null && isSmsModalOpen}
                            loadingText="جاري..."
                            onClick={handleSendSMS}
                            _hover={{ bgGradient: 'linear(to-r, blue.500, purple.500)' }}
                        >
                            إرسال لأولياء الأمور
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={() => setIsSmsModalOpen(false)}
                            borderRadius="full"
                            size="md"
                            color="gray.600"
                            _hover={{ bg: 'gray.100' }}
                        >
                            إلغاء
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default AttendanceControl;
